// Contexte global pour l'onboarding
//
// Pourquoi un contexte et pas juste un state local dans OnboardingGate ?
// → Le bouton "Revoir le didacticiel" se trouve dans Profile (enfant de Layout,
//   lui-même enfant de OnboardingGate). Il ne peut pas remonter un état vers
//   OnboardingGate via props sans percer l'arborescence entière.
//   Un Context est la bonne solution React : le Provider vit au-dessus de
//   OnboardingGate ET de Layout, les deux ont accès au même état.

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import { supabase } from '@/lib/supabase'

// ─── Clé localStorage ────────────────────────────────────────────────────────

export const ONBOARDING_KEY = 'selah_onboarding_done'

// ─── Types ───────────────────────────────────────────────────────────────────

interface OnboardingCtxValue {
  /** true = l'overlay onboarding doit être affiché */
  showOnboarding: boolean
  /** true = vérification Supabase en cours (ne pas rendre l'app avant) */
  isChecking: boolean
  /** Appeler depuis n'importe quel enfant pour relancer le didacticiel */
  replay: () => void
  /** Appeler à la fin (bouton "Commencer" ou "Passer") pour fermer */
  dismiss: () => void
}

// ─── Context ─────────────────────────────────────────────────────────────────

const OnboardingCtx = createContext<OnboardingCtxValue>({
  showOnboarding: false,
  isChecking: true,
  replay: () => {},
  dismiss: () => {},
})

// ─── Provider ────────────────────────────────────────────────────────────────

export function OnboardingProvider({ children }: { children: ReactNode }) {
  // Initialisé à false — la vérification async décide de la vraie valeur
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false)
  // true le temps de la vérification (localStorage + Supabase) pour éviter le flash
  const [isChecking, setIsChecking] = useState<boolean>(true)

  // Vérification de l'état onboarding : localStorage en priorité, puis Supabase
  useEffect(() => {
    ;(async () => {
      try {
        // 1. Clé locale présente → onboarding déjà fait, pas besoin d'appel réseau
        if (localStorage.getItem(ONBOARDING_KEY)) {
          setShowOnboarding(false)
          return
        }
        // 2. Vérifier dans Supabase (reconnexion Google sur un nouveau device)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.user) {
          // Pas encore connecté → afficher l'onboarding après connexion
          setShowOnboarding(true)
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data } = await (supabase.from('user_profile') as any)
          .select('onboarding_done')
          .eq('user_id', session.user.id)
          .maybeSingle()
        if (data?.onboarding_done) {
          // L'onboarding a été fait sur un autre appareil → synchroniser localStorage
          localStorage.setItem(ONBOARDING_KEY, '1')
          setShowOnboarding(false)
        } else {
          setShowOnboarding(true)
        }
      } catch {
        // Réseau indisponible → fallback sur localStorage
        setShowOnboarding(!localStorage.getItem(ONBOARDING_KEY))
      } finally {
        setIsChecking(false)
      }
    })()
  }, [])

  const replay = useCallback(() => {
    setShowOnboarding(true)
  }, [])

  const dismiss = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, '1')
    setShowOnboarding(false)
    // Persister dans Supabase pour la synchronisation multi-appareils
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.user) return
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase.from('user_profile') as any)
          .update({ onboarding_done: true })
          .eq('user_id', session.user.id)
      } catch {
        // Non bloquant
      }
    })()
  }, [])

  return (
    <OnboardingCtx.Provider value={{ showOnboarding, isChecking, replay, dismiss }}>
      {children}
    </OnboardingCtx.Provider>
  )
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useOnboarding(): OnboardingCtxValue {
  return useContext(OnboardingCtx)
}
